(() => {
  const initHeroes = (heroes) => {
    if (!heroes || heroes.dataset.cgV2HeroesInitialized === 'true') {
      return;
    }

    const slides = Array.from(heroes.querySelectorAll('[data-cg-v2-heroes-slide]'));
    const previous = heroes.querySelector('[data-cg-v2-heroes-previous]');
    const next = heroes.querySelector('[data-cg-v2-heroes-next]');
    const viewport = heroes.querySelector('[data-cg-v2-heroes-viewport]');
    const status = heroes.querySelector('[data-cg-v2-heroes-status]');
    const autoplayEnabled = heroes.dataset.autoplay === 'true';
    const configuredAutoplayInterval = Number.parseInt(heroes.dataset.autoplayInterval || '6000', 10);
    const autoplayInterval = Number.isInteger(configuredAutoplayInterval)
      ? Math.min(Math.max(configuredAutoplayInterval, 4000), 12000)
      : 6000;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const mobileDragQuery = window.matchMedia('(max-width: 759px)');

    if (!slides.length) {
      return;
    }

    heroes.dataset.cgV2HeroesInitialized = 'true';
    let activeIndex = Number.parseInt(heroes.dataset.activeIndex || '0', 10);
    let autoplayTimer = null;
    let swipeState = null;
    let swipeSettleTimer = null;
    let isAnimating = false;
    let isInView = !('IntersectionObserver' in window);
    let isPointerPaused = false;
    let isFocusPaused = false;
    let isTouchPaused = false;
    let isEditorPaused = false;
    let visibilityObserver = null;

    if (!Number.isInteger(activeIndex) || activeIndex < 0 || activeIndex >= slides.length) {
      activeIndex = 0;
    }

    slides.forEach((slide) => {
      const slideBackground = slide.dataset.slideBackground;
      if (slideBackground) {
        slide.style.setProperty('--cg-v2-heroes-slide-bg', slideBackground);
      }
    });

    const render = (announce = true) => {
      slides.forEach((slide, index) => {
        const isActive = index === activeIndex;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      heroes.dataset.activeIndex = String(activeIndex);

      const activeBackground = slides[activeIndex].dataset.slideBackground;
      if (activeBackground) {
        heroes.style.setProperty('--cg-v2-heroes-bg', activeBackground);
      }

      const activeHeadingColor = slides[activeIndex].dataset.slideHeadingColor;
      if (activeHeadingColor) {
        heroes.style.setProperty('--cg-v2-heroes-heading', activeHeadingColor);
      }

      if (status) {
        const label = slides[activeIndex].dataset.slideLabel || `Flavor ${activeIndex + 1}`;
        status.setAttribute('aria-live', announce ? 'polite' : 'off');
        status.textContent = `${label}. Slide ${activeIndex + 1} of ${slides.length}.`;
      }
    };

    const clearAutoplay = () => {
      if (autoplayTimer !== null) {
        window.clearTimeout(autoplayTimer);
        autoplayTimer = null;
      }
    };

    const canAutoplay = () => (
      autoplayEnabled
      && slides.length > 1
      && isInView
      && !document.hidden
      && !reducedMotion.matches
      && !isPointerPaused
      && !isFocusPaused
      && !isTouchPaused
      && !isEditorPaused
    );

    const scheduleAutoplay = () => {
      clearAutoplay();

      if (!canAutoplay()) {
        return;
      }

      autoplayTimer = window.setTimeout(() => {
        autoplayTimer = null;
        move(1, false);
      }, autoplayInterval);
    };

    const wrappedIndex = (index) => (index + slides.length) % slides.length;

    const clearSwipeSettle = () => {
      if (swipeSettleTimer !== null) {
        window.clearTimeout(swipeSettleTimer);
        swipeSettleTimer = null;
      }
    };

    const resetSwipeVisuals = () => {
      viewport?.classList.remove('is-dragging', 'is-settling', 'is-programmatic');
      slides.forEach((slide) => {
        slide.classList.remove('is-drag-current', 'is-drag-adjacent');
        slide.style.removeProperty('--cg-v2-heroes-drag-x');
      });
    };

    const resumeAfterSwipe = () => {
      const wasTouchPaused = isTouchPaused;
      isTouchPaused = false;
      if (wasTouchPaused) {
        scheduleAutoplay();
      }
    };

    const positionSwipeSlides = (deltaX) => {
      if (!swipeState || !viewport) {
        return;
      }

      const direction = deltaX < 0 ? 1 : -1;
      const adjacentIndex = wrappedIndex(activeIndex + direction);
      const constrainedDelta = Math.max(-swipeState.width, Math.min(swipeState.width, deltaX));

      if (swipeState.adjacentIndex !== null && swipeState.adjacentIndex !== adjacentIndex) {
        const previousAdjacent = slides[swipeState.adjacentIndex];
        previousAdjacent.classList.remove('is-drag-adjacent');
        previousAdjacent.style.removeProperty('--cg-v2-heroes-drag-x');
      }

      const currentSlide = slides[activeIndex];
      const adjacentSlide = slides[adjacentIndex];
      const adjacentStart = direction * swipeState.width;

      viewport.classList.add('is-dragging');
      currentSlide.classList.add('is-drag-current');
      adjacentSlide.classList.add('is-drag-adjacent');
      currentSlide.style.setProperty('--cg-v2-heroes-drag-x', `${constrainedDelta}px`);
      adjacentSlide.style.setProperty('--cg-v2-heroes-drag-x', `${adjacentStart + constrainedDelta}px`);

      swipeState.deltaX = constrainedDelta;
      swipeState.direction = direction;
      swipeState.adjacentIndex = adjacentIndex;
    };

    const finishSwipe = (cancelled = false) => {
      if (!swipeState || !viewport) {
        return;
      }

      const state = swipeState;
      const releaseTime = window.performance.now();

      if (viewport.hasPointerCapture?.(state.pointerId)) {
        viewport.releasePointerCapture(state.pointerId);
      }

      if (state.axis !== 'horizontal' || state.adjacentIndex === null) {
        swipeState = null;
        resetSwipeVisuals();
        resumeAfterSwipe();
        return;
      }

      const distance = Math.abs(state.deltaX);
      const commitDistance = Math.min(Math.max(state.width * 0.28, 88), 132);
      const recentVelocity = releaseTime - state.lastMoveTime <= 100 ? Math.abs(state.velocityX) : 0;
      const deliberateFlick = distance >= 64 && recentVelocity >= 0.55;
      const shouldCommit = !cancelled && (distance >= commitDistance || deliberateFlick);
      const currentSlide = slides[activeIndex];
      const adjacentSlide = slides[state.adjacentIndex];
      const adjacentRestingPosition = state.direction * state.width;
      const settleDuration = reducedMotion.matches ? 0 : 280;

      viewport.classList.remove('is-dragging');
      viewport.classList.add('is-settling');
      void viewport.offsetWidth;

      currentSlide.style.setProperty(
        '--cg-v2-heroes-drag-x',
        `${shouldCommit ? -state.direction * state.width : 0}px`
      );
      adjacentSlide.style.setProperty(
        '--cg-v2-heroes-drag-x',
        `${shouldCommit ? 0 : adjacentRestingPosition}px`
      );

      const completeSwipe = () => {
        swipeSettleTimer = null;

        if (shouldCommit) {
          activeIndex = state.adjacentIndex;
          render();
        }

        swipeState = null;
        resetSwipeVisuals();
        resumeAfterSwipe();
      };

      clearSwipeSettle();
      if (settleDuration === 0) {
        completeSwipe();
      } else {
        swipeSettleTimer = window.setTimeout(completeSwipe, settleDuration);
      }
    };

    const move = (direction, announce = true) => {
      if (slides.length < 2) {
        return;
      }

      if (isAnimating || swipeState) {
        scheduleAutoplay();
        return;
      }

      const nextIndex = wrappedIndex(activeIndex + direction);
      clearAutoplay();

      if (!viewport || reducedMotion.matches) {
        activeIndex = nextIndex;
        render(announce);
        scheduleAutoplay();
        return;
      }

      const width = Math.max(viewport.clientWidth, 1);
      const currentSlide = slides[activeIndex];
      const nextSlide = slides[nextIndex];
      const transitionDuration = 440;

      isAnimating = true;
      currentSlide.classList.add('is-drag-current');
      nextSlide.classList.add('is-drag-adjacent');
      currentSlide.style.setProperty('--cg-v2-heroes-drag-x', '0px');
      nextSlide.style.setProperty('--cg-v2-heroes-drag-x', `${direction * width}px`);
      viewport.classList.add('is-programmatic');
      void viewport.offsetWidth;

      viewport.classList.add('is-settling');
      currentSlide.style.setProperty('--cg-v2-heroes-drag-x', `${-direction * width}px`);
      nextSlide.style.setProperty('--cg-v2-heroes-drag-x', '0px');

      const completeMove = () => {
        swipeSettleTimer = null;
        activeIndex = nextIndex;
        render(announce);
        isAnimating = false;
        resetSwipeVisuals();
        scheduleAutoplay();
      };

      clearSwipeSettle();
      swipeSettleTimer = window.setTimeout(completeMove, transitionDuration);
    };

    previous?.addEventListener('click', () => move(-1));
    next?.addEventListener('click', () => move(1));

    viewport?.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        move(-1);
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        move(1);
      }
    });

    viewport?.addEventListener('pointerdown', (event) => {
      if (
        !mobileDragQuery.matches
        || slides.length < 2
        || isAnimating
        || swipeState
        || swipeSettleTimer !== null
        || !event.isPrimary
        || (event.pointerType === 'mouse' && event.button !== 0)
        || event.target.closest('button')
      ) {
        return;
      }

      swipeState = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        lastX: event.clientX,
        lastY: event.clientY,
        lastMoveTime: window.performance.now(),
        velocityX: 0,
        deltaX: 0,
        axis: null,
        direction: 0,
        adjacentIndex: null,
        width: Math.max(viewport.clientWidth, 1)
      };
    });

    viewport?.addEventListener('pointermove', (event) => {
      if (!swipeState || event.pointerId !== swipeState.pointerId) {
        return;
      }

      const now = window.performance.now();
      const deltaX = event.clientX - swipeState.startX;
      const deltaY = event.clientY - swipeState.startY;
      const moveDuration = now - swipeState.lastMoveTime;
      const absoluteX = Math.abs(deltaX);
      const absoluteY = Math.abs(deltaY);

      if (moveDuration > 0) {
        swipeState.velocityX = (event.clientX - swipeState.lastX) / moveDuration;
      }

      swipeState.lastX = event.clientX;
      swipeState.lastY = event.clientY;
      swipeState.lastMoveTime = now;

      if (swipeState.axis === null) {
        if (absoluteY >= 10 && absoluteY > absoluteX * 1.1) {
          swipeState.axis = 'vertical';
          return;
        }

        if (absoluteX >= 12 && absoluteX > absoluteY * 1.35) {
          swipeState.axis = 'horizontal';
          isTouchPaused = true;
          clearAutoplay();
          viewport.setPointerCapture?.(event.pointerId);
        } else {
          return;
        }
      }

      if (swipeState.axis !== 'horizontal') {
        return;
      }

      event.preventDefault();
      positionSwipeSlides(deltaX);
    }, { passive: false });

    viewport?.addEventListener('pointerup', (event) => {
      if (!swipeState || event.pointerId !== swipeState.pointerId) {
        return;
      }

      if (swipeState.axis === 'horizontal') {
        positionSwipeSlides(event.clientX - swipeState.startX);
      }

      finishSwipe(false);
    });

    viewport?.addEventListener('pointercancel', (event) => {
      if (!swipeState || event.pointerId !== swipeState.pointerId) {
        return;
      }

      finishSwipe(true);
    });

    heroes.addEventListener('mouseenter', () => {
      isPointerPaused = true;
      clearAutoplay();
    });

    heroes.addEventListener('mouseleave', () => {
      isPointerPaused = false;
      scheduleAutoplay();
    });

    heroes.addEventListener('focusin', (event) => {
      if (!event.target.matches?.(':focus-visible')) {
        return;
      }

      isFocusPaused = true;
      clearAutoplay();
    });

    heroes.addEventListener('focusout', (event) => {
      if (heroes.contains(event.relatedTarget)) {
        return;
      }

      if (!isFocusPaused) {
        return;
      }

      isFocusPaused = false;
      scheduleAutoplay();
    });

    const handleVisibilityChange = () => {
      if (document.hidden) {
        clearAutoplay();
      } else {
        scheduleAutoplay();
      }
    };

    const handleReducedMotionChange = () => {
      if (reducedMotion.matches) {
        clearAutoplay();
      } else {
        scheduleAutoplay();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    reducedMotion.addEventListener?.('change', handleReducedMotionChange);

    if ('IntersectionObserver' in window) {
      visibilityObserver = new IntersectionObserver(([entry]) => {
        isInView = Boolean(entry?.isIntersecting && entry.intersectionRatio >= 0.15);

        if (isInView) {
          scheduleAutoplay();
        } else {
          clearAutoplay();
        }
      }, { threshold: [0, 0.15] });

      visibilityObserver.observe(heroes);
    }

    heroes.cgV2ShowBlock = (block) => {
      const selectedIndex = slides.indexOf(block);
      if (selectedIndex < 0) {
        return;
      }

      clearSwipeSettle();
      isAnimating = false;
      isTouchPaused = false;
      swipeState = null;
      resetSwipeVisuals();
      activeIndex = selectedIndex;
      render();
      isEditorPaused = true;
      clearAutoplay();
    };

    heroes.cgV2ResumeAutoplay = () => {
      isEditorPaused = false;
      scheduleAutoplay();
    };

    heroes.cgV2HeroesDestroy = () => {
      clearAutoplay();
      clearSwipeSettle();
      resetSwipeVisuals();
      visibilityObserver?.disconnect();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      reducedMotion.removeEventListener?.('change', handleReducedMotionChange);
    };

    render(false);
    scheduleAutoplay();
  };

  const init = (scope = document) => {
    scope.querySelectorAll('[data-cg-v2-heroes]').forEach(initHeroes);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelector('[data-cg-v2-heroes]')?.cgV2HeroesDestroy?.();
  });
  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target.closest('[data-cg-v2-heroes-slide]');
    const heroes = slide?.closest('[data-cg-v2-heroes]');
    heroes?.cgV2ShowBlock?.(slide);
  });
  document.addEventListener('shopify:block:deselect', (event) => {
    event.target.closest('[data-cg-v2-heroes]')?.cgV2ResumeAutoplay?.();
  });
})();
